"use client";
import {EyeIcon} from "@/components/shared";
import {useEffect, useState} from "react";
import Loader from "../Loader";

interface tripFailureSummary {
  vehicleDidntOperate: {
    pickup: number;
    drop: number;
  };
  vehicleIssue: {
    pickup: number;
    drop: number;
  };
  offlineOnTrip: {
    pickup: number;
    drop: number;
  };
  operatedOnWrongRoute: {
    pickup: number;
    drop: number;
  };
}

const defaultTripFailureSummary: tripFailureSummary = {
  vehicleDidntOperate: {
    pickup: 0,
    drop: 0
  },
  vehicleIssue: {
    pickup: 0,
    drop: 0
  },
  offlineOnTrip: {
    pickup: 0,
    drop: 0
  },
  operatedOnWrongRoute: {
    pickup: 0,
    drop: 0
  }
};
interface TripFailureWidgetProps {
  isLoading?: boolean;
  instituteuserids?: any;
  tripFailure: tripFailureSummary;
  openPopup?: (title: popupParams) => void;
}

const SHADE_BG = ["#DC2626", "#F87171", "#FCA5A5", "#FECDD3"] as const;
const PICKUP_COLOR = "#2563EB";
const DROP_COLOR = "#B45309";

// filterTypeId must match the "type" innerFilter option ids in report-menu.ts (they are not sequential)
const CATEGORY_META = [
  {
    label: "Vehicle Didn't Operate",
    sublabel: "The assigned vehicle did not run for this trip.",
    filterTypeId: 1
  },
  {
    label: "Vehicle Issue / Breakdown",
    sublabel:
      "An alternate vehicle was arranged, but it also did not complete the trip.",
    filterTypeId: 2
  },
  {
    label: "Operated on Wrong Route",
    sublabel:
      "The vehicle operated on a different route instead of the assigned one.",
    filterTypeId: 3
  },
  {
    label: "Vehicle Offline",
    sublabel: "Vehicle lost communication.",
    filterTypeId: 4
  }
];

const TripFailureWidget: React.FC<TripFailureWidgetProps> = ({
  isLoading,
  tripFailure,
  openPopup
}) => {
  const [tripFailureData, setTripFailureData] = useState<tripFailureSummary>(
    defaultTripFailureSummary
  );

  useEffect(() => {
    if (tripFailure) setTripFailureData(tripFailure);
  }, [tripFailure]);

  const categories = [
    tripFailureData.vehicleDidntOperate,
    tripFailureData.vehicleIssue,
    tripFailureData.operatedOnWrongRoute,
    tripFailureData.offlineOnTrip
  ];
  const pickupTotal = categories.reduce((sum, c) => sum + c.pickup, 0);
  const dropTotal = categories.reduce((sum, c) => sum + c.drop, 0);
  const totalFailures = pickupTotal + dropTotal;
  const maxCount = Math.max(...categories.map(c => c.pickup + c.drop), 1);

  return (
    <div className="rounded-xl border bg-neo-white p-3">
      {/* Header */}
      <div className="mb-2 flex items-center justify-between">
        <div className="text-lg font-bold text-default-800">Trip Failure</div>
        <div className="mr-1 flex items-center">
          <h5
            className="cursor-pointer"
            title="View"
            onClick={() => openPopup?.({title: "Trip Failure"})}
          >
            <EyeIcon size={18} />
          </h5>
        </div>
      </div>

      {isLoading ? (
        <Loader />
      ) : (
        <>
          {/* Total failures bar */}
          <div className="mb-3 flex  rounded-xl border p-2 py-1">
            <div className="flex w-full items-center justify-between">
              <div className="flex flex-row items-center gap-4">
                <div className="flex items-center gap-3 py-1">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-lg font-bold text-[#FC2A3D]">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 33 33"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M16.5 0C7.3872 0 0 7.3872 0 16.5C0 25.6128 7.3872 33 16.5 33C25.6128 33 33 25.6128 33 16.5C33 7.3872 25.6128 0 16.5 0ZM18.0872 19.5684L18.538 6.47989C18.538 5.49163 17.7999 4.68307 16.8966 4.68307H16.1034C15.2009 4.68307 14.462 5.49163 14.462 6.47989L14.9128 19.5684C14.9128 20.5242 15.6275 21.3061 16.5 21.3061C17.3733 21.3061 18.0872 20.5242 18.0872 19.5684ZM16.5 23.9948C15.3062 23.9948 14.339 24.9629 14.339 26.1559C14.339 27.3497 15.307 28.3169 16.5 28.3169C17.6938 28.3169 18.6618 27.3489 18.6618 26.1559C18.661 24.9629 17.6938 23.9948 16.5 23.9948Z"
                        fill="#EF4444"
                      />
                    </svg>
                  </span>
                  <div className="flex flex-row items-center gap-2">
                    <div className="text-sm font-semibold">Total</div>
                    <div className=" text-lg font-bold text-[#FC2A3D]">
                      {totalFailures}
                    </div>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span
                  className="flex items-center gap-1.5 rounded-full border-1 px-2 py-1 text-xs font-bold"
                  style={{
                    borderColor: PICKUP_COLOR,
                    color: PICKUP_COLOR
                  }}
                >
                  Pickup
                  <span
                    className="flex h-5 w-5 items-center justify-center rounded-full text-[11px] text-white"
                    style={{background: PICKUP_COLOR}}
                  >
                    {pickupTotal}
                  </span>
                </span>
                <span
                  className="flex items-center gap-1.5 rounded-full border-1 px-2 py-1 text-xs font-bold"
                  style={{
                    borderColor: DROP_COLOR,
                    color: DROP_COLOR
                  }}
                >
                  Drop
                  <span
                    className="flex h-5 w-5 items-center justify-center rounded-full text-[11px] text-white"
                    style={{background: DROP_COLOR}}
                  >
                    {dropTotal}
                  </span>
                </span>
              </div>
            </div>
          </div>

          {/* Category list */}
          <div className="space-y-3">
            {categories.map((cat, i) => {
              const meta = CATEGORY_META[i];
              const total = cat.pickup + cat.drop;
              return (
                <div key={i}>
                  <div
                    className="mb-1 flex cursor-pointer items-center justify-between gap-2"
                    title={`View ${meta.label} details`}
                    onClick={() =>
                      openPopup?.({
                        title: "Trip Failure",
                        innerFilterDefaultsIicon: {
                          type: [meta.filterTypeId]
                        }
                      })
                    }
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="h-2 w-2 flex-shrink-0 rounded-full"
                        style={{background: SHADE_BG[i]}}
                      />
                      <div className="text-sm font-semibold text-default-800">
                        {meta.label}
                      </div>
                    </div>
                    <div className="mr-3 flex w-[152px] flex-shrink-0 items-center gap-2 whitespace-nowrap text-xs font-bold tabular-nums">
                      <span style={{color: PICKUP_COLOR}}>
                        Pickup -{" "}
                        <span className="inline-block min-w-[1.4em] text-right">
                          {cat.pickup}
                        </span>
                      </span>
                      <span className="text-default-300">|</span>
                      <span style={{color: DROP_COLOR}}>
                        Drop -{" "}
                        <span className="inline-block min-w-[1.4em] text-right">
                          {cat.drop}
                        </span>
                      </span>
                    </div>
                  </div>
                  <div className="mb-1 truncate pl-4 text-xs text-default-400">
                    {meta.sublabel}
                  </div>
                  <div className="px-3">
                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${(total / maxCount) * 100}%`,
                          background: SHADE_BG[i]
                        }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default TripFailureWidget;
